import PropTypes from 'prop-types';
import React from 'react';

import { useTranslation } from 'react-i18next';
import moment from 'moment-timezone';

import { Icon, Table, TableBody, TableHeader, TableRow, TableHeaderCell, TableCell, Status, Select3, highlightText } from '@weezevent/nacre';

import Styles from '../../styles/rate/rateCreation.module.css';

export const StatusEvent = React.memo(({ status, removed = null }) => {
    const { t } = useTranslation();

    const color = React.useMemo(() => {
        if (removed) {
            return 'red';
        }

        if (status === 'closed') {
            return 'grey';
        }

        return status === 'draft' ? 'orange' : 'green';
    }, [status]);

    return <Status text={t(`ticket.rate_template.${removed ? 'deleted' : status}`)} color={color} />;
});

function LinkedEventRow({ organizationId, event, events = [] }) {
    const { t } = useTranslation();

    let link = `/ticket/O${organizationId}/E${event.id}/settings/rates`;
    // If there's only one event (thus rate) generated from this template
    // we can redirect the user right to the rate page instead of the list
    if (event.rates.length === 1) {
        link += `/${event.rates[0].id}`;
    }

    let { start_date, timezone, publication_status, removed } = events.find(e => e.id === event.id) || {};

    return (
        <TableRow key={event.id}>
            <TableCell>{event.name}</TableCell>
            <TableCell>{event.id}</TableCell>
            <TableCell>{new Date(start_date).toLocaleDateString([], { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: timezone })}</TableCell>
            <TableCell style={{ textAlign: 'center' }} className={'events-picker-status'}>
                <StatusEvent status={publication_status} removed={removed} />
            </TableCell>
            <TableCell style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                {t('ticket.rate_template.table_to_link_events_already_linked')} <Icon name="link" />
            </TableCell>
            <TableCell style={{ textAlign: 'center' }}>
                <a target={'_blank'} rel="noopener noreferrer" className="form-link-event" href={link}>
                    <Icon name="external-link" />
                </a>
            </TableCell>
        </TableRow>
    );
}

LinkedEventRow.propTypes = {
    organizationId: PropTypes.number.isRequired,
    event: PropTypes.object.isRequired,
    events: PropTypes.array.isRequired
};

export default function EventsPicker({ organizationId, events = [], selected = [], onSave = () => {}, rate }) {
    const { t } = useTranslation();

    const options = React.useMemo(
        () =>
            events
                .filter(event => {
                    if (event.removed) {
                        return false;
                    }

                    if (event.publication_status === 'closed') {
                        return false;
                    }

                    if (event.end_date && event.end_date < moment.utc().format()) {
                        return false;
                    }
                    if (rate?.events.find(linkedEvent => linkedEvent.id === event.id)) {
                        return false;
                    }
                    if (selected.includes(event.id)) return false;
                    return true;
                })
                .map(event => ({
                    event,
                    text: event.name,
                    value: event.pk
                })),
        [events, rate, selected]
    );

    const selectedEvents = events.filter(e => selected.includes(e.id));

    const deselectEvent = React.useCallback(
        id => {
            onSave(selected.filter(value => value !== id));
        },
        [selected]
    );

    const handleChange = React.useCallback(
        id => {
            onSave(selected.concat([id]));
        },
        [selected, onSave]
    );
    return (
        <div>
            <Select3
                className={'event-picker-select-3'}
                onChange={handleChange}
                options={options}
                placeholder={t('ticket.rate_template.events_picker_dropdown_placeholder')}
                listItemsRenderer={eventListItemsRenderer}
            />
            <div className={'events-picker-count-events'}>
                {t('ticket.rate_template.events_picker_count_events_to_link', { count: selectedEvents.length })}
                {', '}
                {t('ticket.rate_template.events_picker_count_linked_events', { count: rate?.events?.length || 0 })}
            </div>
            {(rate?.events?.length > 0 || selectedEvents.length > 0) && (
                <div className={Styles['table-settings-events-container']}>
                    <Table basic celled responsive={false}>
                        <TableHeader>
                            <TableRow>
                                <TableHeaderCell>{t('ticket.rate_template.table_to_link_events_name')}</TableHeaderCell>
                                <TableHeaderCell>{t('ticket.rate_template.table_to_link_events_id')}</TableHeaderCell>
                                <TableHeaderCell>{t('ticket.rate_template.table_to_link_events_date')}</TableHeaderCell>
                                <TableHeaderCell style={{ textAlign: 'center' }}>{t('ticket.rate_template.table_to_link_events_event_status')}</TableHeaderCell>
                                <TableHeaderCell style={{ textAlign: 'center' }}>{t('ticket.rate_template.table_to_link_events_rate_status')}</TableHeaderCell>
                                <TableHeaderCell style={{ width: '100px' }}></TableHeaderCell>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {selectedEvents.map(event => (
                                <TableRow key={event.id}>
                                    <TableCell>{event.name}</TableCell>
                                    <TableCell>{event.id}</TableCell>
                                    <TableCell>
                                        {new Date(event.start_date).toLocaleDateString([], { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: event.timezone })}
                                    </TableCell>
                                    <TableCell style={{ textAlign: 'center' }} className={'events-picker-status'}>
                                        <StatusEvent status={event.publication_status} />
                                    </TableCell>
                                    <TableCell style={{ textAlign: 'center' }}>-</TableCell>
                                    <TableCell style={{ textAlign: 'center' }}>
                                        <button className="form-remove-event" onClick={() => deselectEvent(event.id)}>
                                            {t('ticket.common.action.remove')}
                                        </button>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {rate?.events?.map(event => (
                                <LinkedEventRow key={event.id} events={events} organizationId={organizationId} event={event} />
                            ))}
                        </TableBody>
                    </Table>
                </div>
            )}
        </div>
    );
}

function eventListItemsRenderer(option, words) {
    // renders an event as a select3 option, and highlights the event name
    return (
        <div className={'events-picker-options'}>
            <div>
                <div className={'events-picker-options-name'}>{highlightText(option.event.name, words)}</div>
                <div className={'events-picker-options-date'}>
                    {new Date(option.event.start_date).toLocaleDateString([], {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        timeZone: option.event.timezone
                    }) +
                        ' - ' +
                        option.event.id}
                </div>
            </div>
            <div>
                <StatusEvent status={option.event.publication_status} />
            </div>
        </div>
    );
}

EventsPicker.propDefaults = {
    rate: null
};

EventsPicker.propTypes = {
    onSave: PropTypes.func.isRequired,
    events: PropTypes.array.isRequired,
    selected: PropTypes.array.isRequired,
    organizationId: PropTypes.number.isRequired,
    formatLabel: PropTypes.func.isRequired,
    rate: PropTypes.object
};
